import React, { useState } from 'react';
import convert from 'convert-units';
import Breadcrumbs from 'components/Breadcrumbs';
import { calculateInertization as calculateInertizationAPI } from 'utils/methods/calculators';

const InertizationCalculator = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  
  const tabs = [
    'Equipment Details',
    'Material Details & Oxygen Levels',
    'Inert Gas Selection',
    'Inertization Method',
    'Review & Calculate'
  ];

  // Form state
  const [formData, setFormData] = useState({
    containerType: 'storagetank',
    volume: 0,
    volumeUnit: 'm3',
    isEquipmentVacuum: false,
    materialType: 'na',
    initialOxygen: 21,
    targetOxygen: 5,
    inertGas: 'n2',
    isInertGasPure: false,
    inertOxygen: 0,
    inertizationMethod: 'pressure',
    reliefSetPressure: 1,
    reliefSetPressureUnit: 'bar',
    setPressure: 0,
    setPressureUnit: 'bar',
    maxVacuumLevel: 0,
    maxVacuumLevelUnit: 'bar',
    setVacuum: 0,
    setVacuumUnit: 'bar',
    inertFlowRate: 0,
    inertFlowRateUnit: 'm3/s',
    pressureFirst: true
  });

  // Results state
  const [results, setResults] = useState({
    requiredCycles: 0,
    purgeTime: 0,
    purgeTimeUnit: 's',
    calculationParameters: {},
    assumptions: []
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const convertUnits = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return parseFloat(value);
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 0;
    
    try {
      // Handle unit mapping for convert-units package
      const unitMapping = {
        // Volume units
        'm3': 'm3',
        'km3': 'km3', 
        'ft3': 'ft3',
        'l': 'l',
        'kl': 'kl',
        'gal': 'gal',
        
        // Pressure units
        'bar': 'bar',
        'pa': 'Pa',
        'kpa': 'kPa',
        'mpa': 'MPa',
        'psi': 'psi',
        'atm': 'atm',
        'mmhg': 'mmHg',
        'inhg': 'inHg',
        
        // Volume Flow Rate units
        'm3/s': 'm3/s',
        'm3/min': 'm3/min',
        'm3/h': 'm3/h',
        'l/s': 'l/s',
        'l/min': 'l/min',
        'l/h': 'l/h',
        'ft3/s': 'ft3/s',
        'ft3/min': 'ft3/min',
        'ft3/h': 'ft3/h',
        'gal/s': 'gal/s',
        'gal/min': 'gal/min',
        'gal/h': 'gal/h',
        
        // Time units
        's': 's',
        'min': 'min',
        'h': 'h',
        'ms': 'ms'
      };
      
      const mappedFromUnit = unitMapping[fromUnit] || fromUnit;
      const mappedToUnit = unitMapping[toUnit] || toUnit;
      
      return convert(numValue).from(mappedFromUnit).to(mappedToUnit);
    } catch (error) {
      console.warn(`Unit conversion failed from ${fromUnit} to ${toUnit}:`, error);
      // Fallback to custom conversion for unsupported units
      return customConvertUnits(numValue, fromUnit, toUnit);
    }
  };

  // Fallback custom conversion for units not supported by convert-units
  const customConvertUnits = (value, fromUnit, toUnit) => {
    const conversionFactors = {
      // Volume
      'm3': 1,
      'km3': 1000000,
      'ft3': 0.0283168,
      'l': 0.001,
      'kl': 1,
      'gal': 0.00378541,
      
      // Pressure (all to bar base)
      'bar': 1,
      'pa': 0.00001,
      'kpa': 0.01,
      'mpa': 10,
      'psi': 0.0689476,
      'atm': 1.01325,
      'mmhg': 0.00133322,
      'inhg': 0.0338639,
      
      // Volume Flow Rate (all to m³/s base)
      'm3/s': 1,
      'm3/min': 1/60,
      'm3/h': 1/3600,
      'l/s': 0.001,
      'l/min': 0.001/60,
      'l/h': 0.001/3600,
      'ft3/s': 0.0283168,
      'ft3/min': 0.0283168/60,
      'ft3/h': 0.0283168/3600,
      'gal/s': 0.00378541,
      'gal/min': 0.00378541/60,
      'gal/h': 0.00378541/3600,
      
      // Time (all to seconds base)
      's': 1,
      'min': 60,
      'h': 3600,
      'ms': 0.001
    };
    
    const fromFactor = conversionFactors[fromUnit] || 1;
    const toFactor = conversionFactors[toUnit] || 1;
    
    return (parseFloat(value) * fromFactor) / toFactor;
  };

  const updateVolume = (event) => {
    const newUnit = event.target.value;
    const convertedValue = convertUnits(formData.volume, formData.volumeUnit, newUnit);
    handleInputChange('volumeUnit', newUnit);
    handleInputChange('volume', parseFloat(convertedValue.toPrecision(4)));
  };

  const updateReliefSetPressure = (newUnit) => {
    if (!formData.reliefSetPressure || formData.reliefSetPressure === '') {
      setFormData(prev => ({
        ...prev,
        reliefSetPressureUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.reliefSetPressure, formData.reliefSetPressureUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      reliefSetPressure: convertedValue.toPrecision(4),
      reliefSetPressureUnit: newUnit
    }));
  };

  const updateSetPressure = (newUnit) => {
    if (!formData.setPressure || formData.setPressure === '') {
      setFormData(prev => ({
        ...prev,
        setPressureUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.setPressure, formData.setPressureUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      setPressure: convertedValue.toPrecision(4),
      setPressureUnit: newUnit
    }));
  };

  const updateMaxVacuumLevel = (newUnit) => {
    if (!formData.maxVacuumLevel || formData.maxVacuumLevel === '') {
      setFormData(prev => ({
        ...prev,
        maxVacuumLevelUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.maxVacuumLevel, formData.maxVacuumLevelUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      maxVacuumLevel: convertedValue.toPrecision(4),
      maxVacuumLevelUnit: newUnit
    }));
  };

  const updateSetVacuum = (newUnit) => {
    if (!formData.setVacuum || formData.setVacuum === '') {
      setFormData(prev => ({
        ...prev,
        setVacuumUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.setVacuum, formData.setVacuumUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      setVacuum: convertedValue.toPrecision(4),
      setVacuumUnit: newUnit
    }));
  };

  const updateInertFlowRate = (newUnit) => {
    if (!formData.inertFlowRate || formData.inertFlowRate === '') {
      setFormData(prev => ({
        ...prev,
        inertFlowRateUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(formData.inertFlowRate, formData.inertFlowRateUnit, newUnit);
    setFormData(prev => ({
      ...prev,
      inertFlowRate: convertedValue.toPrecision(4),
      inertFlowRateUnit: newUnit
    }));
  };

  const updatePurgeTimeUnit = (newUnit) => {
    if (!results.purgeTime || results.purgeTime === 0) {
      setResults(prev => ({
        ...prev,
        purgeTimeUnit: newUnit
      }));
      return;
    }
    const convertedValue = convertUnits(results.purgeTime, results.purgeTimeUnit, newUnit);
    setResults(prev => ({
      ...prev,
      purgeTime: parseFloat(convertedValue.toPrecision(4)),
      purgeTimeUnit: newUnit
    }));
  };

  const nextTab = () => {
    if (activeTab < tabs.length - 1) {
      setActiveTab(activeTab + 1);
    }
  };

  const prevTab = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };

  const resetResults = () => {
    setShowResults(false);
  };

  const verifyPH = (event) => {
    const value = parseFloat(event.target.value);
    const valueInBar = convertUnits(value, formData.setPressureUnit, 'bar');
    const reliefPressureInBar = convertUnits(formData.reliefSetPressure, formData.reliefSetPressureUnit, 'bar');
    const oneAtmInBar = convertUnits(1, 'atm', 'bar');
    
    // Check if set pressure is greater than 1 atm
    if (valueInBar <= oneAtmInBar) {
      event.target.classList.add('border-red-500');
      event.target.classList.remove('border-green-500');
    } 
    // Check if set pressure is less than relief pressure
    else if (valueInBar >= reliefPressureInBar) {
      event.target.classList.add('border-red-500');
      event.target.classList.remove('border-green-500');
    } 
    else {
      event.target.classList.remove('border-red-500');
      event.target.classList.add('border-green-500');
    }
  };

  const verifyPL = (event) => {
    const value = parseFloat(event.target.value);
    const valueInBar = convertUnits(value, formData.setVacuumUnit, 'bar');
    const maxVacuumInBar = convertUnits(formData.maxVacuumLevel, formData.maxVacuumLevelUnit, 'bar');
    const oneAtmInBar = convertUnits(1, 'atm', 'bar');
    
    // Check if set vacuum is less than max vacuum (should be greater)
    if (valueInBar <= maxVacuumInBar) {
      event.target.classList.add('border-red-500');
      event.target.classList.remove('border-green-500');
    } 
    // Check if set vacuum is greater than or equal to 1 atm (should be less)
    else if (valueInBar >= oneAtmInBar) {
      event.target.classList.add('border-red-500');
      event.target.classList.remove('border-green-500');
    } 
    else {
      event.target.classList.remove('border-red-500');
      event.target.classList.add('border-green-500');
    }
  };

  const isVacuumMethod = () => (formData.inertizationMethod === 'vacuum' || formData.inertizationMethod === 'pressurevacuum');
  const isPressureMethod = () => (formData.inertizationMethod === 'pressure' || formData.inertizationMethod === 'pressurevacuum');

  const verifyInputs = () => {
    // Basic validation
    if (formData.volume <= 0) {
      alert('Equipment volume should be greater than 0');
      return false;
    }

    if (!formData.initialOxygen || formData.initialOxygen < 0 || formData.initialOxygen > 100) {
      alert('Initial oxygen concentration should be between 0 and 100%');
      return false;
    }

    if (!formData.targetOxygen || formData.targetOxygen < 0 || formData.targetOxygen > 100) {
      alert('Target oxygen concentration should be between 0 and 100%');
      return false;
    }

    if (formData.targetOxygen >= formData.initialOxygen) {
      alert('Target oxygen concentration should be less than initial oxygen concentration');
      return false;
    }

    // Method-specific validations
    if (formData.inertizationMethod === 'continuouspurging') {
      if (formData.inertFlowRate <= 0) {
        alert('Inert gas flow rate should be greater than 0');
        return false;
      }
      return true;
    }

    // Pressure method validations
    if (isPressureMethod()) {
      const setPressureInBar = convertUnits(formData.setPressure, formData.setPressureUnit, 'bar');
      const reliefPressureInBar = convertUnits(formData.reliefSetPressure, formData.reliefSetPressureUnit, 'bar');
      const oneAtmInBar = convertUnits(1, 'atm', 'bar');
      
      if (setPressureInBar <= oneAtmInBar) {
        alert('Set pressure should be greater than 1 atm (atmospheric pressure)');
        return false;
      }

      if (setPressureInBar >= reliefPressureInBar) {
        alert('Set pressure should be less than relief device set pressure');
        return false;
      }
    }

    // Vacuum method validations
    if (isVacuumMethod()) {
      const setVacuumInBar = convertUnits(formData.setVacuum, formData.setVacuumUnit, 'bar');
      const maxVacuumInBar = convertUnits(formData.maxVacuumLevel, formData.maxVacuumLevelUnit, 'bar');
      const oneAtmInBar = convertUnits(1, 'atm', 'bar');
      
      if (!formData.isEquipmentVacuum) {
        alert('Equipment must be vacuum rated for vacuum methods');
        return false;
      }

      if (setVacuumInBar > oneAtmInBar) {
        alert('Set vacuum pressure should be less than 1 atm (atmospheric pressure)');
        return false;
      }

      if (setVacuumInBar <= maxVacuumInBar) {
        alert('Set vacuum pressure should be greater than maximum vacuum level (less vacuum means higher pressure)');
        return false;
      }
    }

    return true;
  };

  const calculateInertization = async (event) => {
    event.preventDefault();
    
    if (!verifyInputs()) {
      return;
    }

    setIsCalculating(true);
    setShowResults(false);

    try {
      // Convert volume to standard units (m³) for API call
      const volumeInM3 = convertUnits(formData.volume, formData.volumeUnit, 'm3');

      // Convert pressure values to standard units (bar) for API call
      const reliefSetPressureInBar = convertUnits(formData.reliefSetPressure, formData.reliefSetPressureUnit, 'bar');
      const setPressureInBar = convertUnits(formData.setPressure, formData.setPressureUnit, 'bar');
      const maxVacuumLevelInBar = convertUnits(formData.maxVacuumLevel, formData.maxVacuumLevelUnit, 'bar');
      const setVacuumInBar = convertUnits(formData.setVacuum, formData.setVacuumUnit, 'bar');

      // Convert flow rate to standard units (m³/s) for API call
      const inertFlowRateInM3s = convertUnits(formData.inertFlowRate, formData.inertFlowRateUnit, 'm3/s');

      // Prepare the request data according to the backend API structure
      const requestData = {
        container: {
          volume: volumeInM3
        },
        initialOxygen: parseFloat(formData.initialOxygen),
        targetOxygen: parseFloat(formData.targetOxygen),
        inertGasProps: {
          name: formData.inertGas,
          percentage: 100, // Assuming 100% when pure, this might need adjustment
          isPure: !formData.isInertGasPure,
          oxygenPercentage: parseFloat(formData.inertOxygen) || 0
        },
        isEquipmentVacuumGrade: formData.isEquipmentVacuum,
        materialType: formData.materialType,
        inertFlowRate: parseFloat(inertFlowRateInM3s) || 0,
        setPressure: setPressureInBar,
        reliefSetPressure: reliefSetPressureInBar,
        maxVacuumLevel: maxVacuumLevelInBar,
        setVacuum: setVacuumInBar,
        method: formData.inertizationMethod,
        pressureFirst: formData.pressureFirst
      };

      console.log('Sending request:', requestData);

      // Make API call using the established methods approach
      const response = await calculateInertizationAPI(requestData);
      const result = response.data;
      console.log('API Response:', result);

      if (result.status === 'success') {
        setResults({
          requiredCycles: result.data.required_cycles || 0,
          purgeTime: result.data.purge_time || 0,
          purgeTimeUnit: 's', // Default unit from API is seconds
          calculationParameters: result.data.calculation_parameters || {},
          assumptions: result.data.assumptions || [
            "Only considered Nitrogen (N<sub>2</sub>) as the inert gas",
            "Assumes inert gas follows the ideal gas law",
            "No gas leakage (Inert gas loss during cycles is not accounted)",
            "Designed for standalone equipment and ignores the interdependencies in multi-vessel systems",
            "Assumes uniform inert gas purity",
            "Assumes rapid equilibrium after each inertization cycle",
            "Considered only the target concentration, safety based on flammability limits is not considered",
            "The mass of nitrogen required is also not calculated"
          ]
        });

        setShowResults(true);
      } else {
        alert('Calculation failed: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error calculating inertization:', error);
      alert('Error calculating inertization. Please check your inputs and try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleMaterialUpdate = (event) => {
    const materialType = event.target.value;
    handleInputChange('materialType', materialType);
    
    switch (materialType) {
      case 'pyrophoric':
        handleInputChange('targetOxygen', 1);
        break;
      case 'highflammable':
        handleInputChange('targetOxygen', 2);
        break;
      default:
        handleInputChange('targetOxygen', 5);
        break;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100">
      {/* Main Content */}
      <div className="container-fluid px-4 py-8">
        <div className="max-w-full mx-auto">
          {/* Header Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Inertization Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate the number of inertization cycles required in process equipment to prevent fire.
            </p>
          </div>

          <Breadcrumbs />
          
          {/* Desktop Layout - Side by Side */}
          <div className="md:flex gap-8 md:h-[calc(100vh-250px)]">
            {/* Left Side - Questions (Scrollable) */}
            <div className="lg:w-2/3 bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="h-full p-6 md:p-8 overflow-y-auto">
                <form onSubmit={calculateInertization} className="space-y-8">
                  {/* Tab Navigation */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 lg:p-6 rounded-lg mb-6">
                      <h3 className="font-medium text-gray-800 mb-3">Assumptions & Limitations</h3>
                      <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
                        {(
                          <>
                            <li>Assumes inert gas follows the ideal gas law</li>
                            <li>No gas leakage (Inert gas loss during cycles is not accounted)</li>
                            <li>Designed for standalone equipment and ignores the interdependencies in multi-vessel systems</li>
                            <li>Assumes uniform inert gas purity</li>
                            <li>Assumes rapid equilibrium after each inertization cycle</li>
                            <li>Considered only the target concentration, safety based on flammability limits is not considered</li>
                            <li>The mass of nitrogen required is also not calculated</li>
                          </>
                        )}
                      </ul>
                    </div>
                  <div className="mb-8">
                    <div className="flex flex-wrap border-b border-gray-200 pb-4 gap-2 flex-col md:flex-row">
                      {tabs.map((tab, index) => (
                        <button 
                          key={index}
                          type="button"
                          onClick={() => setActiveTab(index)}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors mr-2 ${
                            activeTab === index 
                              ? 'bg-blue-600 text-white border-b-2 border-blue-600' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <span className="mr-1">{index + 1}.</span> {tab}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tab Content Equipment */}
                  <div className="tab-content">
                    {/* Equipment Details */}
                    {activeTab === 0 && (
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                          Equipment Details
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 space-y-6 md:space-y-0">
                          {/* Equipment Type */}
                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Equipment Type
                              <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={formData.containerType}
                              onChange={(e) => handleInputChange('containerType', e.target.value)}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                              required
                            >
                              <option value="storagetank">Storage Tank</option>
                              <option value="processvessel">Process Vessel</option>
                              <option value="pipeline">Pipeline</option>
                              <option value="reactor">Reactor</option>
                            </select>
                          </div>

                          {/* Equipment Volume */}
                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Equipment Volume
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <div className="relative flex items-center gap-4">
                                <input
                                  value={formData.volume}
                                  onChange={(e) => handleInputChange('volume', parseFloat(e.target.value) || 0)}
                                  type="number"
                                  required
                                  min="0"
                                  step="0.1"
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all hover:border-blue-300"
                                  placeholder="Enter volume"
                                />
                                <select
                                  value={formData.volumeUnit}
                                  onChange={updateVolume}
                                  className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white w-full"
                                >
                                  <option value="m3">m³</option>
                                  <option value="km3">km³</option>
                                  <option value="ft3">ft³</option>
                                  <option value="l">Liters</option>
                                  <option value="kl">Kiloliters</option>
                                  <option value="gal">Gallons</option>
                                </select>
                              </div>
                              <p className="mt-2 text-sm text-gray-500">Enter the total volume of your container</p>
                            </div>
                          </div>

                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Is the equipment vacuum rated?
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={formData.isEquipmentVacuum}
                                  onChange={(e) => handleInputChange('isEquipmentVacuum', e.target.checked)}
                                  className="form-checkbox text-blue-500"
                                />
                                <label className="text-sm text-gray-700">Yes.</label>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                          <button
                            type="button"
                            className={`px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors ${
                              activeTab === 0 ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            onClick={prevTab}
                            disabled={activeTab === 0}
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            onClick={nextTab}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Material Details & Oxygen Levels */}
                    {activeTab === 1 && (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Material Details*/}
                          <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                              Chemical Details
                            </h2>

                            <div className="space-y-6">
                              {/* Equipment Type */}
                              <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Material Type
                                  <span className="text-red-500">*</span>
                                </label>
                                <select
                                  value={formData.materialType}
                                  onChange={handleMaterialUpdate}
                                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                  required
                                >
                                  <option value="na">General</option>
                                  <option value="highflammable">Highly flammable</option>
                                  <option value="pyrophoric">Pyrophoric</option>
                                </select>
                              </div>
                            </div>
                          </div>

                          {/* Oxygen Concentrations */}
                          <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                              Oxygen Levels
                            </h2>

                            <div className="grid md:grid-cols-1 gap-6">
                              <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Initial Oxygen Concentration (%)
                                  <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                  <input
                                    value={formData.initialOxygen}
                                    onChange={(e) => handleInputChange('initialOxygen', parseFloat(e.target.value) || 0)}
                                    type="number"
                                    required
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                    placeholder="Enter initial O₂"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">%</span>
                                </div>
                                <p className="mt-2 text-sm text-gray-500">Typical air contains 21% oxygen</p>
                              </div>

                              <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Target Oxygen Concentration (%)
                                  <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                  <input
                                    value={formData.targetOxygen}
                                    onChange={(e) => handleInputChange('targetOxygen', parseFloat(e.target.value) || 0)}
                                    type="number"
                                    required
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                    placeholder="Enter target O₂"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">%</span>
                                </div>
                                <p className="mt-2 text-sm text-gray-500">Recommended: 5% or lower for safety</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                          <button
                            type="button"
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            onClick={prevTab}
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            onClick={nextTab}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Inert Gas Selection */}
                    {activeTab === 2 && (
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                          Inert Gas Selection
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 space-y-6 md:space-y-0">
                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select Inert Gas Type
                              <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={formData.inertGas}
                              onChange={(e) => handleInputChange('inertGas', e.target.value)}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                            >
                              <option value="n2">Nitrogen (N₂)</option>
                            </select>
                            <p className="mt-2 text-sm text-gray-500">Choose the inert gas you'll use for the process</p>
                          </div>

                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Is the inert gas pure? (0% oxygen)
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={formData.isInertGasPure}
                                  onChange={(e) => handleInputChange('isInertGasPure', e.target.checked)}
                                  className="form-checkbox text-blue-500"
                                />
                                <label className="text-sm text-gray-700">No, it's impure.</label>
                              </div>
                            </div>

                            {formData.isInertGasPure && (
                              <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Amount of O<sub>2</sub> in inert gas (%)
                                  <span className="text-red-500">*</span>
                                </label>
                                <div className="flex items-center space-x-4">
                                  <div className="flex items-center space-x-2">
                                    <input
                                      value={formData.inertOxygen}
                                      onChange={(e) => handleInputChange('inertOxygen', parseFloat(e.target.value) || 0)}
                                      type="number"
                                      required
                                      min="0"
                                      max="100"
                                      step="0.1"
                                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pl-4 pr-12 transition-all hover:border-blue-300"
                                      placeholder="Enter inert O₂"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                          <button
                            type="button"
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            onClick={prevTab}
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            onClick={nextTab}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Inertization Method */}
                    {activeTab === 3 && (
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                          Inertization Method
                        </h2>

                        <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Inertization Method
                            <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.inertizationMethod}
                            onChange={(e) => {
                              handleInputChange('inertizationMethod', e.target.value);
                              resetResults();
                            }}
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                            required
                          >
                            <option value="pressure">Pressure Method</option>
                            {formData.isEquipmentVacuum && <option value="vacuum">Vacuum Method</option>}
                            {formData.isEquipmentVacuum && <option value="pressurevacuum">Combination of Pressure & Vacuum Swing</option>}
                            <option value="continuouspurging">Continuous Purging</option>
                          </select>
                          <p className="mt-2 text-sm text-gray-500">Choose the method that best suits your process requirements</p>
                        </div>

                        {/* Pressure/Vacuum Order Selection for Combination Method */}
                        {formData.inertizationMethod === 'pressurevacuum' && (
                          <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Inertization Cycle Order
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="flex flex-col space-y-3">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="radio"
                                  id="pressureFirst"
                                  name="cycleOrder"
                                  checked={formData.pressureFirst === true}
                                  onChange={() => handleInputChange('pressureFirst', true)}
                                  className="form-radio text-blue-500 h-4 w-4"
                                />
                                <label htmlFor="pressureFirst" className="text-sm text-gray-700 cursor-pointer">
                                  <span className="font-medium">Pressure First</span> - Start with pressurization, then vacuum
                                </label>
                              </div>
                              <div className="flex items-center space-x-3">
                                <input
                                  type="radio"
                                  id="vacuumFirst"
                                  name="cycleOrder"
                                  checked={formData.pressureFirst === false}
                                  onChange={() => handleInputChange('pressureFirst', false)}
                                  className="form-radio text-blue-500 h-4 w-4"
                                />
                                <label htmlFor="vacuumFirst" className="text-sm text-gray-700 cursor-pointer">
                                  <span className="font-medium">Vacuum First</span> - Start with vacuum, then pressurization
                                </label>
                              </div>
                            </div>
                            <p className="mt-2 text-sm text-gray-500">Select the order of inertization cycles for the combination method</p>
                          </div>
                        )}

                        {/* Method specific fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 space-y-6 md:space-y-0">
                          {(formData.inertizationMethod === 'pressure' || formData.inertizationMethod === 'pressurevacuum') && (
                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Relief Device Set Pressure
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-2 flex-col md:flex-row">
                                <input
                                  value={formData.reliefSetPressure}
                                  onChange={(e) => handleInputChange('reliefSetPressure', parseFloat(e.target.value) || 0)}
                                  type="number"
                                  required
                                  min="1"
                                  step="0.1"
                                  className="flex-1 p-3 border rounded-lg transition-all w-full"
                                  placeholder="Enter pressure"
                                />
                                <select 
                                  value={formData.reliefSetPressureUnit}
                                  onChange={(e) => updateReliefSetPressure(e.target.value)}
                                  className="w-20 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value="bar">bar</option>
                                  <option value="psi">psi</option>
                                  <option value="kpa">kPa</option>
                                  <option value="mpa">MPa</option>
                                  <option value="atm">atm</option>
                                  <option value="pa">Pa</option>
                                </select>
                              </div>
                            </div>
                          )}

                          {(formData.inertizationMethod === 'pressure' || formData.inertizationMethod === 'pressurevacuum') && (
                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Set Pressure for Inertization
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-2 flex-col md:flex-row">
                                <input
                                  value={formData.setPressure}
                                  onChange={(e) => {
                                    handleInputChange('setPressure', parseFloat(e.target.value) || 0);
                                    verifyPH(e);
                                  }}
                                  type="number"
                                  required
                                  min="0"
                                  step="0.1"
                                  className="flex-1 p-3 border rounded-lg transition-all w-full"
                                  placeholder="Enter pressure"
                                />
                                <select 
                                  value={formData.setPressureUnit}
                                  onChange={(e) => updateSetPressure(e.target.value)}
                                  className="w-20 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value="bar">bar</option>
                                  <option value="psi">psi</option>
                                  <option value="kpa">kPa</option>
                                  <option value="mpa">MPa</option>
                                  <option value="atm">atm</option>
                                  <option value="pa">Pa</option>
                                </select>
                              </div>
                              <p className="mt-2 text-sm text-gray-500">Must be greater than 1 atm but less than relief device set pressure</p>
                            </div>
                          )}

                          {(formData.inertizationMethod === 'vacuum' || formData.inertizationMethod === 'pressurevacuum') && (
                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Maximum Vacuum Pressure For Equipment
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-2 flex-col md:flex-row">
                                <input
                                  value={formData.maxVacuumLevel}
                                  onChange={(e) => handleInputChange('maxVacuumLevel', parseFloat(e.target.value) || 0)}
                                  type="number"
                                  required
                                  min="0"
                                  step="0.01"
                                  className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all hover:border-blue-300 w-full"
                                  placeholder="Enter pressure"
                                />
                                <select 
                                  value={formData.maxVacuumLevelUnit}
                                  onChange={(e) => updateMaxVacuumLevel(e.target.value)}
                                  className="w-20 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value="bar">bar</option>
                                  <option value="psi">psi</option>
                                  <option value="kpa">kPa</option>
                                  <option value="mpa">MPa</option>
                                  <option value="atm">atm</option>
                                  <option value="pa">Pa</option>
                                  <option value="mmhg">mmHg</option>
                                  <option value="inhg">inHg</option>
                                </select>
                              </div>
                              <p className="mt-2 text-sm text-gray-500">Minimum pressure the equipment can safely handle (lowest vacuum)</p>
                            </div>
                          )}

                          {(formData.inertizationMethod === 'vacuum' || formData.inertizationMethod === 'pressurevacuum') && (
                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Set Vacuum Pressure
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-2 flex-col md:flex-row">
                                <input
                                  value={formData.setVacuum}
                                  onChange={(e) => {
                                    handleInputChange('setVacuum', parseFloat(e.target.value) || 0);
                                    verifyPL(e);
                                  }}
                                  type="number"
                                  required
                                  min="0"
                                  step="0.01"
                                  className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all hover:border-blue-300 w-full"
                                  placeholder="Enter pressure"
                                />
                                <select 
                                  value={formData.setVacuumUnit}
                                  onChange={(e) => updateSetVacuum(e.target.value)}
                                  className="w-20 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value="bar">bar</option>
                                  <option value="psi">psi</option>
                                  <option value="kpa">kPa</option>
                                  <option value="mpa">MPa</option>
                                  <option value="atm">atm</option>
                                  <option value="pa">Pa</option>
                                  <option value="mmhg">mmHg</option>
                                  <option value="inhg">inHg</option>
                                </select>
                              </div>
                              <p className="mt-2 text-sm text-gray-500">Must be greater than max vacuum level but less than 1 atm</p>
                            </div>
                          )}

                          {formData.inertizationMethod === 'continuouspurging' && (
                            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                What is the flow rate of inert gas available?
                                <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-2 flex-col md:flex-row">
                                <input
                                  value={formData.inertFlowRate}
                                  onChange={(e) => handleInputChange('inertFlowRate', parseFloat(e.target.value) || 0)}
                                  type="number"
                                  required
                                  min="0"
                                  step="0.001"
                                  className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all hover:border-blue-300 w-full"
                                  placeholder="Enter flow rate"
                                />
                                <select 
                                  value={formData.inertFlowRateUnit}
                                  onChange={(e) => updateInertFlowRate(e.target.value)}
                                  className="w-24 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value="m3/s">m³/s</option>
                                  <option value="m3/min">m³/min</option>
                                  <option value="m3/h">m³/h</option>
                                  <option value="l/s">L/s</option>
                                  <option value="l/min">L/min</option>
                                  <option value="l/h">L/h</option>
                                  <option value="ft3/s">ft³/s</option>
                                  <option value="ft3/min">ft³/min</option>
                                  <option value="ft3/h">ft³/h</option>
                                  <option value="gal/s">gal/s</option>
                                  <option value="gal/min">gal/min</option>
                                  <option value="gal/h">gal/h</option>
                                </select>
                              </div>
                              <p className="mt-2 text-sm text-gray-500">Enter the volumetric flow rate of inert gas available for continuous purging</p>
                            </div>
                          )}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between mt-8">
                          <button
                            type="button"
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            onClick={prevTab}
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            onClick={nextTab}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Review & Calculate */}
                    {activeTab === 4 && (
                      <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                          Review & Calculate
                        </h2>

                        <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow">
                          <h3 className="text-md font-medium text-gray-800 mb-4">Review Your Inputs</h3>

                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-700">Equipment Type:</p>
                                <p className="text-sm text-gray-600">{formData.containerType}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">Equipment Volume:</p>
                                <p className="text-sm text-gray-600">{formData.volume} {formData.volumeUnit}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-700">Material Type:</p>
                                <p className="text-sm text-gray-600">{formData.materialType}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">Vacuum Rated:</p>
                                <p className="text-sm text-gray-600">{formData.isEquipmentVacuum ? 'Yes' : 'No'}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-700">Initial Oxygen Level:</p>
                                <p className="text-sm text-gray-600">{formData.initialOxygen}%</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">Target Oxygen Level:</p>
                                <p className="text-sm text-gray-600">{formData.targetOxygen}%</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-700">Inert Gas Type:</p>
                                <p className="text-sm text-gray-600">{formData.inertGas === 'n2' ? 'Nitrogen (N₂)' : formData.inertGas}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">Inertization Method:</p>
                                <p className="text-sm text-gray-600">{formData.inertizationMethod}</p>
                              </div>
                            </div>

                            {formData.inertizationMethod === 'pressurevacuum' && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm font-medium text-gray-700">Cycle Order:</p>
                                  <p className="text-sm text-gray-600">{formData.pressureFirst ? 'Pressure First' : 'Vacuum First'}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Calculate Button */}
                        <button
                          type="submit"
                          disabled={isCalculating}
                          className={`w-full py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 text-lg font-medium shadow-lg ${
                            isCalculating
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                          }`}
                        >
                          {isCalculating ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                              <span>Calculating...</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-calculator"></i>
                              <span>Calculate Inertization Requirements</span>
                            </>
                          )}
                        </button>

                        {/* Navigation Buttons */}
                        <div className="flex justify-start mt-8">
                          <button
                            type="button"
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            onClick={prevTab}
                          >
                            Previous
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Right Side - Results (Scrollable) */}
            <div className="mt-8 md:mt-0 lg:w-1/3 bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="h-full p-6 md:p-8 overflow-y-auto">
                {isCalculating ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 space-y-4 py-12">
                    <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                    <p className="text-xl font-medium">Calculating...</p>
                    <p className="text-gray-400">Please wait while we process your inertization requirements</p>
                  </div>
                ) : !showResults ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 space-y-4 py-12">
                    <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                      <i className="fas fa-calculator text-4xl text-blue-500"></i>
                    </div>
                    <p className="text-xl font-medium">Ready to Calculate</p>
                    <p className="text-gray-400">Enter the required information and click calculate to see results</p>
                  </div>
                ) : (
                  <div className="animate-fadeIn">
                    {/* Results Content */}
                    <h2 className="text-2xl font-semibold text-gray-800 mb-6 sticky top-0 bg-white pt-2 pb-4 z-10">
                      Calculation Results
                    </h2>

                    {/* Main Result Box */}
                    {formData.inertizationMethod === 'pressurevacuum' && (
                      <>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 lg:p-6 rounded-lg mb-6">
                          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="text-4xl lg:text-5xl font-bold text-blue-600">{results.requiredCycles}</div>
                              <div className="text-lg lg:text-xl font-medium text-blue-500">cycles</div>
                            </div>
                            <div className="text-sm text-gray-600">Number of cycles {formData.pressureFirst ? 'after first pressurization' : 'after first vacuum'}</div>
                          </div>
                        </div>
                      </>
                    )}

                    {(formData.inertizationMethod === 'pressure' || formData.inertizationMethod === 'vacuum') && (
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 lg:p-6 rounded-lg mb-6">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="text-4xl lg:text-5xl font-bold text-blue-600">{results.requiredCycles}</div>
                            <div className="text-lg lg:text-xl font-medium text-blue-500">cycles</div>
                          </div>
                          <div className="text-sm text-gray-600">Number of Inertization Cycles</div>
                        </div>
                      </div>
                    )}

                    {formData.inertizationMethod === 'continuouspurging' && (
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 lg:p-6 rounded-lg mb-6">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="text-4xl lg:text-5xl font-bold text-blue-600">{results.purgeTime}</div>
                            <div className="flex items-center gap-2">
                              <select 
                                value={results.purgeTimeUnit}
                                onChange={(e) => updatePurgeTimeUnit(e.target.value)}
                                className="text-lg lg:text-xl font-medium text-blue-500 bg-transparent border-none focus:outline-none cursor-pointer hover:bg-blue-100 rounded px-1"
                              >
                                <option value="s">seconds</option>
                                <option value="min">minutes</option>
                                <option value="h">hours</option>
                                <option value="ms">milliseconds</option>
                              </select>
                            </div>
                          </div>
                          <div className="text-sm text-gray-600">Time of purging</div>
                        </div>
                      </div>
                    )}

                    {/* Additional calculation parameters if provided by API */}
                    {results.calculationParameters && Object.keys(results.calculationParameters).length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg mb-6">
                        <h4 className="font-medium text-gray-800 mb-3">Calculation Details</h4>
                        <div className="space-y-2">
                          {Object.entries(results.calculationParameters).map(([key, value], index) => (
                            <div key={index} className="flex justify-between items-center text-sm">
                              <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                              <span className="font-medium text-gray-900">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InertizationCalculator;
